import React, { useState, useEffect, useCallback, useRef } from "react";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import PlanSummary from "./components/PlanSummary/PlanSummaryContainer";
import ProcessPathTabs from "./components/ProcessPathTabs";
import Chart from "./components/ChartContainer";
import MetricsSection from "../../components/MetricsSection";
import ShiftTotalsSection from "../../components/ShiftTotalsSection/ShiftTotalsSectionContainer";
import Footer from "../../components/Footer";
import { setNavigationEnabled } from "../../store/slices/navigationSlice";
import { buildGetShiftPlanOutputRequest, GetShiftPlanOutputRequest, GetShiftPlanOutputResponse, ShiftSummaryCategory } from "./model";
import "./index.scss";
import ShiftDataTable from "../../components/ShiftDataTable";
import RatesRosterLoader from "../../components/RatesRosterLoader/RatesRosterLoader";
import { POLLING_INTERVAL_IN_MS, MAX_RETRIES } from "./constants";
import { PlanStatus } from "../../utils/constants";
import {useAppDispatch} from "../../store";
import {showError, showNotification} from "../../store/slices/errorSlice";
import {createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab} from "../../services/shiftPlanService";
import { PlanVersion } from "../../types/shiftPlan";
import {setPlanId, setShiftPlanData} from "../../store/slices/shiftPlanSlice";
import {useNavigate} from "react-router-dom";
import { ROUTES } from "../../utils/constants";

// Import sub-page components for review tabs
import RosterAndRates from "../rosterAndRates";
import InboundTrailerVolume from "../inboundTrailerVolume";
import VolumesAndWipContainer from "../volumesAndWip/VolumesAndWipContainer";

interface ReviewAndPublishStateProps {
    shiftPlanOutput?: GetShiftPlanOutputResponse;
    planId: string | null;
    date: string | null;
    shiftType: string | null;
    planType: string | null;
    stationCode: string | null;
}

interface ReviewAndPublishDispatchProps {
    getShiftPlanOutput: (getShiftPlanOutputRequest: GetShiftPlanOutputRequest) => void;
    publishShiftPlan: (planId: string) => Promise<any>;
}

type Props = ReviewAndPublishStateProps & ReviewAndPublishDispatchProps;

/**
 * Review and Publish component
 */
const ReviewAndPublish: React.FC<Props> = ({
  shiftPlanOutput,
  planId,
  date,
  shiftType,
  planType,
  stationCode,
  getShiftPlanOutput,
  publishShiftPlan
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [planDataLoading, setPlanDataLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ShiftSummaryCategory | undefined>(undefined);
  const [activeReviewTab, setActiveReviewTab] = useState<string>("shift-plan");

  // Disable navigation when component mounts, enable when it unmounts
  useEffect(() => {
    dispatch(setNavigationEnabled(false));

    return () => {
      dispatch(setNavigationEnabled(true));
    };
  }, [dispatch]);


  // Start polling when component mounts and required data is available
  useEffect(() => {
    if (!planId || !date || !stationCode || !shiftType) {
      return;
    }

    // Reset state whenever input changes
    setRetryCount(0);
    setPlanDataLoading(true);

    // Clear old interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const fetchData = () => {
      getShiftPlanOutput(
          buildGetShiftPlanOutputRequest(planId!, date!, stationCode!, shiftType!)
      );
    };

    // First API call
    fetchData();

    // Start interval for subsequent calls
    const id: ReturnType<typeof setInterval> = setInterval(() => {
      setRetryCount(prev => {
        // If next retry would exceed max retries → stop polling
        if (prev + 1 >= MAX_RETRIES) {
          clearInterval(id);
          pollingIntervalRef.current = null;
          setPlanDataLoading(false);

          dispatch(showError({
            title: "Failed to Fetch Shift Plan Data",
            message: "Unable to fetch Shift Plan Data. Please try again later."
          }));

          return prev + 1;
        }

        // Continue polling
        fetchData();
        return prev + 1;
      });
    }, POLLING_INTERVAL_IN_MS);

    pollingIntervalRef.current = id;

    // Cleanup on unmount or dependency change
    return () => {
      clearInterval(id);
      pollingIntervalRef.current = null;
      setPlanDataLoading(false);
    };

  }, [planId, date, stationCode, shiftType]);

  useEffect(() => {
    if (shiftPlanOutput?.planStatus === PlanStatus.PENDING_APPROVAL) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      setPlanDataLoading(false);
    }
  }, [shiftPlanOutput]);

  // Update selected category when shift plan data changes
  useEffect(() => {
    if (shiftPlanOutput?.shiftSummary && shiftPlanOutput.shiftSummary.length > 0) {
      // Set default to first category if no category is selected or current selection is not available
      const currentCategoryExists = selectedCategory && 
        shiftPlanOutput.shiftSummary.some(cat => cat.heading === selectedCategory.heading);
      
      if (!currentCategoryExists) {
        setSelectedCategory(shiftPlanOutput.shiftSummary[0]);
      }
    }
  }, [shiftPlanOutput?.shiftSummary, selectedCategory]);

  // Handle tab change from ProcessPathTabs
  const handleTabChange = useCallback((categoryId: string) => {
    if (shiftPlanOutput?.shiftSummary) {
      const category = shiftPlanOutput.shiftSummary.find(cat => cat.heading === categoryId);
      if (category) {
        setSelectedCategory(category);
      }
    }
  }, [shiftPlanOutput?.shiftSummary]);



  // Handle publish
  const handlePublish = async () => {
    try {
      if (!planId) {
        dispatch(showError({
          title: "Publishing Error",
          message: "Plan ID is required to publish."
        }));
        return;
      }
      
      const result = await publishShiftPlan(planId);

      // Access the response from the thunk's payload
      const response = result.payload;

      if (response && response.status === PlanStatus.APPROVED) {
        console.log("Plan published successfully")
        dispatch(showNotification({
          title: "Published Successfully",
          message: "Shift plan published successfully!",
          type: "success"
        }));
        
        // Enable navigation before navigating away
        dispatch(setNavigationEnabled(true));
        navigate('/');
      }
      
    } catch (error) {
      console.error("Error publishing shift plan:", error);
      dispatch(showError({
        title: "Publishing Failed",
        message: "Failed to publish shift plan. Please try again."
      }));
    }
  };

  const onGenerateButtonClick = async () => {
    setIsGenerating(true);

    try {
      const response = await createShiftPlan(date!,
          getClockWindowNameBasedOnSelectedOption(shiftType!), getPlanTypeBasedOnSelectedTab(planType!), PlanVersion.STATIC_V1);

      if (response && response.status &&
            response.status !== 'FAILED' && response.planId) {
        // Dispatch Redux action with selected data

        dispatch(
            setShiftPlanData({
              date: date!,
              shift: shiftType!,
              plan: planType!
            })
        );


        // Set plan ID in Redux
        dispatch(
            setPlanId({
              planId: response.planId
            })
        );

        // Enable navigation before navigating away
        dispatch(setNavigationEnabled(true));

        // Navigate to the shift plan layout only after successful API call
        navigate(ROUTES.SHIFT_PLAN_ROSTER);
      } else {
        dispatch(showError({
          title: 'Generation Failed',
          message: response?.status === 'FAILED'
            ? 'Shift plan generation failed. Please try again.'
            : 'Unable to create shift plan. Please try again.'
        }));
      }
    } catch (error) {
      console.error('Error generating shift plan:', error);
      dispatch(showError({
        title: 'Generation Failed',
        message: 'Failed to generate shift plan. Please try again.'
      }));
    }

    setIsGenerating(false);
  }

  // Check if we should show full screen loader while polling for planStatus
  // Shows loader when: initial loading OR plan data loading and plan status is not PENDING_APPROVAL
  const shouldShowFullScreenLoader = (planDataLoading && (!shiftPlanOutput || shiftPlanOutput.planStatus !== PlanStatus.PENDING_APPROVAL));
  const disableGenerateButton = !date || !shiftType || !planType;

  return (
    <Box spacingInset="300" className="review-and-publish">
      {shouldShowFullScreenLoader ? (
        <RatesRosterLoader status="loading" type={"plan"} message={"Loading Shift Plan data"}/>
      ) : shiftPlanOutput ? (
        <>
          <Box className="top-section">
            <Box className="header">
              <Box>
                <Heading type="h300" level={2}>
                  Review and Publish
                </Heading>
                <Text>Verify values below and confirm</Text>
              </Box>
            </Box>
            <PlanSummary
                onButtonClick={onGenerateButtonClick}
                buttonLabel={isGenerating ? "Generating..." : "Start New Shift Plan"}
                disableButton={disableGenerateButton}
            />
          </Box>

          {/* Review tabs for navigating between input sections */}
          <Box className="review-tabs-container">
            <TabGroup
              fill="line"
              value={activeReviewTab}
              onChange={setActiveReviewTab}
              aria-labelledby="review-tabs"
            >
              <Tab value="shift-plan">Shift Plan</Tab>
              <Tab value="rates-roster">Rates & Roster</Tab>
              <Tab value="inbound-trailers">Inbound Trailers</Tab>
              <Tab value="volume-buffer">Volume & Buffer</Tab>
            </TabGroup>
          </Box>

          {/* Tab content */}
          {activeReviewTab === "shift-plan" && (
            <>
          <MetricsSection />

          <Box className="timeline">
            <Box type="outline" className="graph">
              <Heading type="h500" level={1}>
                RCC Recommended Volume
              </Heading>
              <Chart />
            </Box>
            <ShiftTotalsSection /> 
           
          </Box>

          <Box>
            <ProcessPathTabs 
              shiftSummary={shiftPlanOutput.shiftSummary}
              onTabChange={handleTabChange}
            />
          </Box>

          <ShiftDataTable selectedCategory={selectedCategory} />
            </>
          )}

          {activeReviewTab === "rates-roster" && (
            <RosterAndRates readOnly={true} forceHideLoader={true} />
          )}

          {activeReviewTab === "inbound-trailers" && (
            <InboundTrailerVolume readOnly={true} />
          )}

          {activeReviewTab === "volume-buffer" && (
            <VolumesAndWipContainer readOnly={true} />
          )}

          <Footer
            onClearOverrides={()=>{}}
            nextButtonDisplay="Publish"
            onNext={handlePublish}
            showClearOverrides={false}
          />
        </>
      ) : (
        <div>No review data available</div>
      )}
    </Box>
  );
};

export default ReviewAndPublish;

import './index.scss';